/**
 * A rope's free end — a hand, or a place to tie off later.
 *
 * It exists only to end a rope, so `bondable` is false, and that one flag is
 * what keeps the joining rule to three lines: a join touching one of these is
 * always a rope, which is also how a plain string from a hook to a weight gets
 * said.
 *
 * Holding and pulling are different physics, not different parts. A hand that
 * HOLDS is a fixed point and the bench works out the force; a hand that pulls
 * with a stated force applies it and the rope's tension is then known.
 */
import { JOINS, numbered, PartKind } from '../registry';
import { ACROSS, DOWN } from '../vec';
import { AppliedForce, Part, Port, RopeContact, World } from '../types';

export interface RopeEndData {
    /** Newtons. Absent means it holds still. */
    pull?: number;
}

export const ropeEnd: PartKind<RopeEndData> = {
    key: 'ropeEnd',
    label: 'Rope end',
    home: 2.2,
    create: (at, id) => ({ id, kind: 'ropeEnd', at, data: {} }),
    ports: (part): Port[] => [{ id: 'end', partId: part.id, role: 'anchorage', joins: JOINS.looseEnd, at: { ...part.at } }],
    mass: () => 0,
    extent: () => ({ x: 0.16, y: 0.16 }),
    /** Holding still is being fixed; pulling leaves it free to pay out rope. */
    freedom: (part) => (part.data.pull === undefined ? { axes: [] } : { axes: [ACROSS, DOWN] }),
    bondable: false,
    keyFacts: (part) => [part.data.pull === undefined ? 'holds' : `pulls ${+part.data.pull.toFixed(2)} N`],
    ropeContact: (part, port): RopeContact => ({ at: port.at }),
    forces: (part): AppliedForce[] =>
        part.data.pull === undefined
            ? []
            : [{ partId: part.id, label: `pull ${part.data.pull.toFixed(0)} N`, vector: { x: 0, y: -part.data.pull } }],
    tag: 'E',
    /**
     * NO name on the bench: its caption is the whole of what it has to say.
     *
     * It drew both — "your hand" above, "holding" below — which is one thing
     * said twice, and the second line is the one that carries information. A
     * rope end is still `E₁` in the readouts, where telling two of them apart
     * matters; on the drawing it says what it is doing and nothing else.
     */
    nameAt: () => null,
    name: (part, w) => numbered(part, w, 'E'),
};
